<!--
  Renewal alarms settings — Karim, 17-18 Sep: "add alert 1... alert2... and so
  on... each alarm I can stop if request['s] renewal status is changed to
  [x]... I can edit or remove any alarm and his setting from setting, also to
  write down the alert email message with alarm."

  Unlimited alarms per product (crm_renewal_alert_rules), each independently
  editable/removable, with its own stop-stage list and email content. See
  CrmRenewalAlertRuleController for the API this talks to.
-->
<template>
  <div
    v-if="visible"
    class="ra-modal"
  >
    <div
      class="ra-modal__backdrop"
      @click="$emit('close')"
    />
    <div class="ra-modal__dialog">
      <div class="ra-modal__head">
        <div>
          <h5 class="ra-modal__title">
            Renewal alarms
          </h5>
          <p class="ra-modal__sub">
            {{ productLabel }}
          </p>
        </div>
        <button
          type="button"
          class="ra-modal__close"
          @click="$emit('close')"
        >
          <feather-icon
            icon="XIcon"
            size="18"
          />
        </button>
      </div>

      <div class="ra-modal__body">
        <div
          v-if="loading"
          class="ra-loading"
        >
          <div
            class="spinner-border text-primary"
            role="status"
          >
            <span class="sr-only">Loading…</span>
          </div>
        </div>

        <template v-else>
          <div
            v-if="!rules.length && !editing"
            class="ra-empty"
          >
            No alarms set up for this product yet — it will never send a renewal reminder until you add one.
          </div>

          <div
            v-for="rule in rules"
            :key="rule.id"
            class="ra-rule"
          >
            <div class="ra-rule__row">
              <div class="ra-rule__info">
                <strong>{{ rule.label || ('Alarm #' + rule.id) }}</strong>
                <span class="ra-rule__meta">
                  {{ rule.daysBeforeExpiry }} days before expiry ·
                  stops at: {{ (rule.effectiveStopStages || []).join(', ') || 'default closed stages' }}
                  <span
                    v-if="!rule.active"
                    class="ra-badge ra-badge--off"
                  >inactive</span>
                </span>
              </div>
              <div class="ra-rule__actions">
                <button
                  type="button"
                  class="ra-btn ra-btn--mini"
                  @click="startEdit(rule)"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="13"
                  /> Edit
                </button>
                <button
                  type="button"
                  class="ra-btn ra-btn--mini ra-btn--danger"
                  @click="removeRule(rule)"
                >
                  <feather-icon
                    icon="Trash2Icon"
                    size="13"
                  /> Remove
                </button>
              </div>
            </div>
            <details class="ra-rule__preview">
              <summary>Email content</summary>
              <div><strong>Subject:</strong> {{ rule.emailSubject }}</div>
              <div
                class="ra-rule__body"
                v-html="rule.emailBody"
              />
            </details>
          </div>

          <!-- Add / edit form -->
          <div
            v-if="editing"
            class="ra-form"
          >
            <h6 class="ra-form__title">
              {{ editing.id ? 'Edit alarm' : 'New alarm' }}
            </h6>

            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Label (info)</label>
                <input
                  v-model="editing.label"
                  type="text"
                  class="form-control"
                  placeholder="e.g. First reminder"
                >
              </div>
              <div class="form-group col-md-6">
                <label>Days before expiry *</label>
                <input
                  v-model.number="editing.daysBeforeExpiry"
                  type="number"
                  min="0"
                  class="form-control"
                >
              </div>
            </div>

            <div class="form-group">
              <label>Stop this alarm once the renewal reaches any of these stages</label>
              <v-select
                v-model="editing.stopOnStages"
                multiple
                taggable
                push-tags
                :options="stopStageOptions"
                placeholder="Leave empty to use the default closed stages"
              />
              <small class="text-muted">Default if left empty: won, renewed, rejected, lost, closed, resolved.</small>
            </div>

            <div class="form-group">
              <label>Email subject *</label>
              <input
                v-model="editing.emailSubject"
                type="text"
                class="form-control"
              >
            </div>
            <div class="form-group">
              <label>Email message *</label>
              <textarea
                v-model="editing.emailBody"
                class="form-control"
                rows="6"
              />
              <small
                v-pre
                class="text-muted"
              >Placeholders: <code>{{client}}</code>, <code>{{certificate}}</code>, <code>{{expiry}}</code>, <code>{{days}}</code></small>
            </div>

            <div class="form-group">
              <label class="ra-checkbox">
                <input
                  v-model="editing.active"
                  type="checkbox"
                > Active
              </label>
            </div>

            <div class="ra-form__actions">
              <button
                type="button"
                class="ra-btn"
                :disabled="saving"
                @click="cancelEdit"
              >
                Cancel
              </button>
              <button
                type="button"
                class="ra-btn ra-btn--primary"
                :disabled="saving || !canSave"
                @click="saveRule"
              >
                {{ saving ? 'Saving…' : 'Save alarm' }}
              </button>
            </div>
          </div>

          <button
            v-else
            type="button"
            class="ra-btn ra-btn--add"
            @click="startNew"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
            /> Add alarm
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import vSelect from 'vue-select'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'RenewalAlarmsModal',
  components: { vSelect },
  props: {
    visible: { type: Boolean, default: false },
    productId: { type: [Number, String], default: null },
    productLabel: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      saving: false,
      rules: [],
      editing: null,
      stopStageOptions: ['won', 'renewed', 'rejected', 'lost', 'closed', 'resolved', 'Completed'],
    }
  },
  computed: {
    canSave() {
      const e = this.editing
      return !!(e && e.daysBeforeExpiry !== '' && e.daysBeforeExpiry !== null
        && e.emailSubject && e.emailSubject.trim() && e.emailBody && e.emailBody.trim())
    },
  },
  watch: {
    visible(val) {
      if (val && this.productId) this.fetchRules()
      if (!val) this.editing = null
    },
    productId(val) {
      if (val && this.visible) this.fetchRules()
    },
  },
  methods: {
    async fetchRules() {
      this.loading = true
      try {
        const res = await axios.get(`crm/products/${this.productId}/alert-rules`)
        const body = decryptData(res.data.payload)
        this.rules = (body && body.data && body.data.rules) || []
      } catch (e) {
        this.notify('Failed to load alarms', 'danger')
      } finally {
        this.loading = false
      }
    },
    startNew() {
      this.editing = {
        id: null,
        label: '',
        daysBeforeExpiry: 30,
        stopOnStages: [],
        emailSubject: 'Your {{certificate}} is due for renewal',
        emailBody: '<p>Dear {{client}},</p><p>Your <strong>{{certificate}}</strong> expires on {{expiry}} ({{days}} days from today).</p>',
        active: true,
      }
    },
    startEdit(rule) {
      this.editing = {
        id: rule.id,
        label: rule.label || '',
        daysBeforeExpiry: rule.daysBeforeExpiry,
        stopOnStages: rule.stopOnStages || [],
        emailSubject: rule.emailSubject,
        emailBody: rule.emailBody,
        active: rule.active,
      }
    },
    cancelEdit() {
      this.editing = null
    },
    async saveRule() {
      if (!this.canSave) return
      this.saving = true
      const payload = {
        label: this.editing.label || null,
        days_before_expiry: this.editing.daysBeforeExpiry,
        stop_on_stages: this.editing.stopOnStages && this.editing.stopOnStages.length ? this.editing.stopOnStages : [],
        email_subject: this.editing.emailSubject,
        email_body: this.editing.emailBody,
        active: this.editing.active,
      }
      try {
        if (this.editing.id) {
          await axios.put(`crm/renewal-alert-rules/${this.editing.id}`, payload)
        } else {
          await axios.post(`crm/products/${this.productId}/alert-rules`, payload)
        }
        this.editing = null
        await this.fetchRules()
        this.notify('Alarm saved', 'success')
      } catch (e) {
        this.notify('Could not save this alarm', 'danger')
      } finally {
        this.saving = false
      }
    },
    async removeRule(rule) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Remove "${rule.label || (`Alarm #${rule.id}`)}"? This cannot be undone.`)) return
      try {
        await axios.delete(`crm/renewal-alert-rules/${rule.id}`)
        this.rules = this.rules.filter(r => r.id !== rule.id)
        this.notify('Alarm removed', 'success')
      } catch (e) {
        this.notify('Could not remove this alarm', 'danger')
      }
    },
    notify(text, variant) {
      this.$emit('notify', { text, variant })
    },
  },
}
</script>

<style scoped>
.ra-modal { position: fixed; inset: 0; z-index: 1200; display: flex; align-items: center; justify-content: center; }
.ra-modal__backdrop { position: absolute; inset: 0; background: rgba(0,0,0,.45); }
.ra-modal__dialog { position: relative; background: #fff; border-radius: .6rem; width: min(680px, 92vw); max-height: 88vh; overflow-y: auto; box-shadow: 0 10px 40px rgba(0,0,0,.2); }
.ra-modal__head { display: flex; align-items: flex-start; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid #eee; }
.ra-modal__title { margin: 0; font-size: 1.05rem; }
.ra-modal__sub { margin: .15rem 0 0; color: #6e6b7b; font-size: .82rem; }
.ra-modal__close { border: 0; background: transparent; cursor: pointer; }
.ra-modal__body { padding: 1.1rem 1.25rem; }
.ra-empty { color: #6e6b7b; font-size: .88rem; padding: .5rem 0 1rem; }
.ra-rule { border: 1px solid #eee; border-radius: .5rem; padding: .7rem .9rem; margin-bottom: .7rem; }
.ra-rule__row { display: flex; align-items: flex-start; justify-content: space-between; gap: .5rem; }
.ra-rule__info { display: flex; flex-direction: column; gap: .2rem; }
.ra-rule__meta { font-size: .78rem; color: #6e6b7b; }
.ra-badge { display: inline-block; margin-left: .4rem; padding: .05rem .4rem; border-radius: .3rem; font-size: .68rem; }
.ra-badge--off { background: #f4f4f4; color: #999; }
.ra-rule__actions { display: flex; gap: .4rem; flex-shrink: 0; }
.ra-rule__preview { margin-top: .5rem; font-size: .8rem; }
.ra-rule__body { border: 1px dashed #eee; padding: .5rem; margin-top: .3rem; border-radius: .3rem; }
.ra-form { border-top: 1px solid #eee; padding-top: .9rem; margin-top: .5rem; }
.ra-form__title { margin-bottom: .6rem; }
.ra-form__actions { display: flex; justify-content: flex-end; gap: .5rem; margin-top: .6rem; }
.ra-checkbox { display: flex; align-items: center; gap: .4rem; font-weight: normal; }
.ra-btn { border: 1px solid #e4e4ec; background: #fff; border-radius: .35rem; padding: .35rem .7rem; font-size: .82rem; cursor: pointer; display: inline-flex; align-items: center; gap: .3rem; }
.ra-btn--mini { padding: .2rem .5rem; font-size: .74rem; }
.ra-btn--primary { background: #2563eb; border-color: #2563eb; color: #fff; }
.ra-btn--danger { color: #ea5455; border-color: rgba(234,84,85,.35); }
.ra-btn--add { margin-top: .3rem; }
.ra-loading { display: flex; justify-content: center; padding: 2rem 0; }
</style>
